import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Button, Input } from 'reactstrap';
import { gql } from '@apollo/client';
import styled from 'styled-components';
import _ from 'lodash';

import ECONOMIC_SUMMARY_METRICS from '../constants/economicSummaryMetrics';
import { useDataTransforms, useFilterYears } from '../utils/hooks';
import useFarmOrSectionGroupData from '../hooks/useFarmOrSectionGroupData';
import { useFilterContext } from '../contexts/FilterContext';
import MetricsHeader from '../components/layout/MetricsHeader';
import SummarySection from '../components/layout/SummarySection';
import DetailsSection from '../components/layout/DetailsSection';
import Content from '../components/layout/Content';
import OperationsItemsSummary from '../components/economics/OperationsItemsSummary';
import SelectableMetricChart from '../components/economics/SelectableMetricChart';
import { ShrinkContent } from '../components/ShrinkContent';
import Metric from '../components/economics/Metric';
import { asCurrency, asPercentageOrNa } from '../utils/formatters';
import Endash from '../components/basics/Endash';
import ExpensesSummary from '../components/economics/ExpensesSummary';
import CropOutputsSummary from '../components/economics/CropOutputsSummary';
import { getCropNames } from '../utils/farmData';
import { CropDisplayFragment } from '../queries';
import { useSubsidySwitchContext } from '../contexts/SubsidySwitchContext';
import {
  UnitInputWrapper,
  UnitLabel,
} from '../components/templates/template-components';
import { captureEvent } from '../utils/analytics';
import EconomicSummaryTable from '../components/economics/EconomicSummaryTable';
import { LayoutContext } from '../contexts/LayoutContext';
import ArrowIcon from '../components/icons/ArrowIcon';
import { isCropFilterValid } from '../utils/filters';

const QUERY_NAME = 'EconomicSummaryQuery';
const getFragmentName = (applySubsidy = false) =>
  applySubsidy
    ? 'EconomicSummaryFragmentWithSubsidy'
    : 'EconomicSummaryFragment';

const SectionHeader = styled.h2`
  border-bottom: 1px solid var(--border-color);
  padding-bottom: 0.5rem;
  margin: 0 0.5rem var(--cell-padding);
`;

const additionalFieldsWithoutSubsidy = `
  unsubsidizedEconomicSummary: economicSummary(
    applyMargin: true
    applySubsidy: false
  ) {
    capexCostByYear
    capexAndGeneticsCostByYear
    geneticsCostByYear
    operationCostByYear
  }

  unsubsidizedCropAggregates: cropAggregates(
    applyMargin: true
    applySubsidy: false
  ) {
    crop {
      id
      name
    }

    unsubsidizedEconomicSummary: economicSummary(
      applyMargin: true
      applySubsidy: false
    ) {
      capexCostByYear
      capexAndGeneticsCostByYear
      geneticsCostByYear
      operationCostByYear
      ...OpExFields
    }
  }
`;

const getEconomicSummaryFragment = (applySubsidy = false) => gql`
  ${CropDisplayFragment}
  fragment OpExFields on EconomicSummary {
      installationOpex
      managementOpex
      harvestOpex
      marketingOpex
      devInstallationOpex: installationOpex(assignment: "developer")
      ownerInstallationOpex: installationOpex(assignment: "owner")
      devManagementOpex: managementOpex(assignment: "developer")
      ownerManagementOpex: managementOpex(assignment: "owner")
      devHarvestOpex: harvestOpex(assignment: "developer")
      ownerHarvestOpex: harvestOpex(assignment: "owner")
      devMarketingOpex: marketingOpex(assignment: "developer")
      ownerMarketingOpex: marketingOpex(assignment: "owner")
  }
  fragment EconomicSummaryFields on EconomicSummary {
    grossIncomeEbit
    revenueByYear
    capexAndGeneticsCostByYear
    capexCostByYear
    geneticsCostByYear
    operationCostByYear
    cumulativeGrossIncome
    grossIrr
    ...OpExFields
  }
  fragment ${getFragmentName(applySubsidy)} on SectionsAggregate {
    economicSummary(
      applyMargin: true
      applySubsidy: $applySubsidy
    ) {
      ...EconomicSummaryFields
    }
    cropAggregates(
      applyMargin: true
      applySubsidy: $applySubsidy
    ) {
      crop {
        ...CropDisplayFields
      }

      economicSummary(
        applyMargin: true
        applySubsidy: $applySubsidy
      ) {
        ...EconomicSummaryFields
      }

      operationAggregates {
        operation {
          id
          name
          category
          assignment
          contracted
          rented
        }
        hoursOfLaborByYear
        laborCostByYear
        depreciationCostByYear
        developerCostByYear
        fuelCostByYear
        inputCostByYear
        contractOrRentCostByYear
      }
      yieldAggregates {
        yield {
          id
          unit
        }
        yieldByYear
      }
      ecologicalSummary {
        carbonTonsByYear
      }
      inputAggregates {
        input {
          id
          name
        }
        totalCostByYear
      }
      proratedCapexes {
        capex {
          id
          name
          totalCostByYear(
            applyBuffer: true
            applyMargin: true
            applySubsidy: $applySubsidy
          )
        }
        proratedCostByYear
      }
    }

    ${
      // If subsidies are enabled, we also calculate without subsidies so we can compute the difference:
      applySubsidy ? additionalFieldsWithoutSubsidy : ''
    }
  }
`;

const getTotalSubsidyAmount = ({
  economicSummary,
  unsubsidizedEconomicSummary,
  filterYears,
}) => {
  const getTotalCost = ({
    capexAndGeneticsCostByYear = [],
    operationCostByYear = [],
  }) =>
    _.sum(filterYears(capexAndGeneticsCostByYear)) +
    _.sum(filterYears(operationCostByYear));

  return (
    getTotalCost(unsubsidizedEconomicSummary) - getTotalCost(economicSummary)
  );
};

const TablesRow = styled(ShrinkContent)`
  // Leave space below the rightmost column for the Intercom launcher if table is not shown
  ${({ tableShown }) =>
    !tableShown && `> div:last-of-type { padding-bottom: 25px; }`}
`;

const ScrollContent = styled(Content)`
  overflow: scroll;
  max-height: 100%;
  // Prevent Intercom bubble from blocking content if table is shown
  ${({ tableShown }) => tableShown && `padding-bottom: 75px;`}
`;

const ScrollOverlay = styled.div`
  display: flex;
  justify-content: center;
`;

const ScrollButton = styled(Button)`
  position: fixed;
  bottom: 30px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  z-index: 1050;

  &,
  &:hover,
  &:focus,
  &:active {
    color: var(--text-color) !important;
    border-color: var(--overyield-blue) !important;
    font-weight: bold;
  }
  &:hover {
    background-color: var(--active-hover) !important;
  }
`;

const MinHeightDetailsSection = styled(DetailsSection)`
  min-height: ${({ minHeight = 'fit-content' }) => `${minHeight};`};
`;

const InputWithoutSpinner = styled(Input)`
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  &[type='number'] {
    -moz-appearance: textfield;
  }
`;

const EconomicsView = () => {
  const filterYears = useFilterYears();
  const { crop: cropFilter, years: yearsFilter } = useFilterContext().state;
  const { summaryShown, tableShown } = useContext(LayoutContext);
  const { shouldShowSubsidies } = useSubsidySwitchContext();
  const scrollContentRef = useRef();
  const scrollFooterRef = useRef();
  const [scrollButtonVisible, setScrollButtonVisible] = useState(false);

  const { msg: queryMsg, data } = useFarmOrSectionGroupData(
    getEconomicSummaryFragment(shouldShowSubsidies),
    QUERY_NAME,
    getFragmentName(shouldShowSubsidies),
    [
      {
        name: 'applySubsidy',
        typeString: 'Boolean',
        value: shouldShowSubsidies,
      },
    ],
  );

  const getActiveData = useCallback(
    (aggregateData) => {
      if (!cropFilter) return aggregateData;

      const activeCropAggregate = aggregateData.cropAggregates.find(
        ({ crop }) => crop.name === cropFilter,
      );
      if (!activeCropAggregate) return aggregateData;

      const activeUnsubsidizedCropAggregate =
        aggregateData.unsubsidizedCropAggregates?.find(
          ({ crop }) => crop.name === cropFilter,
        ) ?? {};

      return {
        economicSummary: activeCropAggregate.economicSummary,
        unsubsidizedEconomicSummary:
          activeUnsubsidizedCropAggregate.unsubsidizedEconomicSummary,
        cropAggregates: [activeCropAggregate],
        unsubsidizedCropAggregates: aggregateData.unsubsidizedCropAggregates,
      };
    },
    [cropFilter],
  );
  const [
    crops = [],
    {
      unsubsidizedEconomicSummary = {},
      economicSummary = {},
      cropAggregates = [],
      unsubsidizedCropAggregates = [],
    } = {},
  ] = useDataTransforms(data, [getCropNames, getActiveData]);

  const totalSubsidyAmount = getTotalSubsidyAmount({
    economicSummary,
    unsubsidizedEconomicSummary,
    filterYears,
  });

  const {
    geneticsCostByYear = [],
    installationOpex = [],
    grossIncomeEbit = [],
  } = economicSummary;
  const totalGeneticsAndInstallCost =
    _.sum(filterYears(geneticsCostByYear)) +
    _.sum(filterYears(installationOpex));

  const [discountRate, setDiscountRate] = useState(5);

  const grossIncomeEbitForYears = filterYears(grossIncomeEbit);
  const npv = grossIncomeEbitForYears.reduce(
    (sum, c, t) => sum + c / (1 + discountRate / 100.0) ** t,
    0,
  );
  const msg = queryMsg || (cropAggregates.length === 0 ? 'No data' : null);
  const cropFilterValid = isCropFilterValid(data, cropFilter);

  const scrollButtonOnClick = () => {
    if (!scrollFooterRef.current) return;
    scrollFooterRef.current.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const onScroll = () =>
    setScrollButtonVisible(
      scrollContentRef.current &&
        Math.abs(
          scrollContentRef.current.scrollHeight -
            (scrollContentRef.current.scrollTop +
              scrollContentRef.current.clientHeight),
        ) > 1,
    );

  // Update the scroll button visibility when height of scroll content changes
  useEffect(() => onScroll(), [summaryShown, tableShown]);

  return (
    <>
      <MetricsHeader
        crops={crops}
        showCropFilter
        showCumulativeYearSelect
        showTableButton
      >
        Economic Summary
      </MetricsHeader>
      <ScrollContent
        className="pt-3"
        onScroll={onScroll}
        ref={scrollContentRef}
        tableShown={tableShown}
      >
        <SummarySection>
          <SectionHeader>
            {yearsFilter} Year Timeline
            {cropFilterValid ? (
              <>
                <Endash />
                {cropFilter}
              </>
            ) : (
              ''
            )}
          </SectionHeader>
          <div className="d-flex justify-content-between px-2">
            <SelectableMetricChart
              metrics={ECONOMIC_SUMMARY_METRICS}
              data={economicSummary}
              arrayTransform={filterYears}
              msg={msg}
              className="flex-grow-1 flex-shrink-1 min-w-0"
            />
            <div className="p-5 flex-shrink-0">
              <Metric
                value={asPercentageOrNa(
                  economicSummary.grossIrr?.[yearsFilter - 1],
                )}
                label="Internal Rate of Return (IRR)"
                // override the font-small to make the label larger
                labelClassname=""
              />
              <div className="d-flex justify-content-between mt-3">
                <div>
                  <div className="font-small">Net Present Value</div>
                  <span className="font-xl">{asCurrency(npv)}</span>
                </div>
                <div>
                  <div className="font-small">Discount Rate</div>
                  <UnitInputWrapper>
                    <InputWithoutSpinner
                      type="number"
                      step="any"
                      min="0"
                      max="100"
                      value={discountRate}
                      placeholder="0"
                      onChange={(e) => {
                        const newValue = e.target.value;
                        if (newValue !== discountRate) {
                          captureEvent('discount-rate-changed');
                        }
                        setDiscountRate(e.target.value);
                      }}
                      style={{
                        paddingRight: '18px',
                        maxWidth: '60px',
                        display: 'block',
                        margin: '0 auto',
                      }}
                      bsSize="sm"
                    />
                    <UnitLabel title="%" $maxWidth="1.5rem" $right="12px">
                      %
                    </UnitLabel>
                  </UnitInputWrapper>
                </div>
              </div>
              <Metric
                className="mt-3"
                value={asCurrency(totalGeneticsAndInstallCost)}
                label={`Total Genetics & Install Cost (${yearsFilter} yr)`}
                // override the font-small to make the label larger
                labelClassname=""
              />
              {shouldShowSubsidies && !!totalSubsidyAmount && (
                <Metric
                  className="mt-3"
                  value={asCurrency(totalSubsidyAmount)}
                  label={`Total Subsidies (${yearsFilter} yr)`}
                  // override the font-small to make the label larger
                  labelClassname=""
                />
              )}
            </div>
          </div>
        </SummarySection>
        <MinHeightDetailsSection>
          <SectionHeader>Your Overview</SectionHeader>
          {queryMsg && <span className="mx-2 text-muted">{queryMsg}</span>}
          {!msg && (
            <TablesRow className="row" tableShown={tableShown}>
              <OperationsItemsSummary
                className="col-4"
                cropAggregates={cropAggregates}
              />
              <CropOutputsSummary
                className="col-4"
                cropAggregates={cropAggregates}
              />
              <ExpensesSummary
                className="col-4"
                cropAggregates={cropAggregates}
              />
            </TablesRow>
          )}
        </MinHeightDetailsSection>
        {tableShown && (
          <MinHeightDetailsSection minHeight="calc(100vh - 200px)">
            <EconomicSummaryTable
              cropAggregates={cropAggregates}
              economicSummary={economicSummary}
              queryMsg={queryMsg}
              unsubsidizedCropAggregates={unsubsidizedCropAggregates}
              unsubsidizedEconomicSummary={unsubsidizedEconomicSummary}
            />
          </MinHeightDetailsSection>
        )}
        {scrollButtonVisible && (
          <ScrollOverlay>
            <ScrollButton onClick={scrollButtonOnClick}>
              <ArrowIcon width={18} height={18} direction="down" />
            </ScrollButton>
          </ScrollOverlay>
        )}
        <div ref={scrollFooterRef} />
      </ScrollContent>
    </>
  );
};

export default EconomicsView;
